import { FeedParams, FeedResponse, Prompt, Course, Challenge } from '@ai-platform/ai-utils';
import { api, ApiTags } from '../../api';
import {
    transformApiPromptToFrontend,
    transformApiCoursesToFrontend,
    transformApiChallengesToFrontend,
} from './transformers';

const feedApi = api.injectEndpoints({
    endpoints: build => ({
        /**
         * Получить ленту с курсами, челленджами и промптами
         * GET /api/feed
         */
        getFeed: build.query<FeedResponse, FeedParams>({
            query: (params = {}) => ({
                url: '/feed',
                params: {
                    ...params,
                },
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                // Исключаем cursor из ключа кеша для работы merge функции
                const { cursor, limit, ...stableArgs } = queryArgs;

                return stableArgs;
            },
            providesTags: result => {
                const tags: Array<{ type: keyof typeof ApiTags; id: string | number }> = [
                    { type: ApiTags.Feed, id: 'LIST' },
                ];

                if (result) {
                    // Добавляем теги для промптов
                    result.prompts.forEach((prompt: Prompt) => {
                        tags.push({ type: ApiTags.Prompts, id: prompt.id });
                    });

                    // Добавляем теги для курсов
                    result.courses.forEach((course: Course) => {
                        tags.push({ type: ApiTags.Courses, id: course.id });
                    });

                    // Добавляем теги для челленджей
                    result.challenges.forEach((challenge: Challenge) => {
                        tags.push({ type: ApiTags.Challenges, id: challenge.id });
                    });
                }

                return tags;
            },
            merge: (currentCache, newItems) => {
                // Дедупликация промптов
                const existingPromptIds = new Set(currentCache.prompts.map(p => p.id));
                const newPrompts = newItems.prompts.filter(p => !existingPromptIds.has(p.id));
                currentCache.prompts.push(...newPrompts);

                currentCache.next_cursor = newItems.next_cursor;
                currentCache.has_more_prompts = newItems.has_more_prompts;
                currentCache.has_more_courses = newItems.has_more_courses;
                currentCache.has_more_challenges = newItems.has_more_challenges;
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.cursor !== previousArg?.cursor;
            },
            transformResponse: (response: FeedResponse) => {
                response.prompts = response.prompts.map(prompt => {
                    return transformApiPromptToFrontend(prompt);
                });
                response.courses = response.courses.map(prompt => {
                    return transformApiCoursesToFrontend(prompt);
                });
                response.challenges = response.challenges.map(prompt => {
                    return transformApiChallengesToFrontend(prompt);
                });

                return response;
            },
            // Кешируем данные на 1 минут для лучшей производительности
            keepUnusedDataFor: 60,
        }),
    }),
});

export const { useGetFeedQuery } = feedApi;
